import React, { useEffect, useState } from 'react'
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import EventForm from '../EventForm'
import useConfirmationAlert from '../../../../hooks/componentHooks/useConfirmationAlert'
import { gradientColors, textColors } from '../../../Common/Color/Color'
import { fetchEventDetails } from '../../../../redux/event/manageEvents/actions'
import { useAppDispatch, useAppSelector } from '../../../../redux/store'
import {
  selectSelectedEvent,
  selectSelectedEventLoading,
} from '../../../../redux/event/manageEvents/selector'
import { clearSelectedEvent } from '../../../../redux/event/manageEvents/slice'
import { fontSizes } from '../../../Common/Typography/TypographyStyles'

// Define prop types
interface EventUpdateModalProps {
  id: string
  completed: boolean
}

const EventUpdateModal: React.FC<EventUpdateModalProps> = ({ id, completed }) => {
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const dispatch = useAppDispatch()
  const [open, setOpen] = useState(false)
  const [formHasChanges, setFormHasChanges] = useState(false)

  useEffect(() => {
    const fetchEventDetail = async () => {
      if (open) {
        await dispatch(fetchEventDetails(id))
      }
    }
    fetchEventDetail()
  }, [open])

  const eventDetails = useAppSelector(selectSelectedEvent)
  const loading = useAppSelector(selectSelectedEventLoading)

  const handleOpenModal = () => {
    setOpen(true)
  }

  const handleFormChanges = (hasChanges: boolean) => {
    setFormHasChanges(hasChanges)
  }

  const { ConfirmationAlert, openConfirmationAlert } = useConfirmationAlert()

  const handleCloseModal = () => {
    if (formHasChanges) {
      openConfirmationAlert({
        title: 'Discard changes',
        text: 'Are you sure you want to discard your changes?',
        onConfirm: () => {
          setOpen(false)
          dispatch(clearSelectedEvent())
        },
      })
    } else {
      setOpen(false)
      dispatch(clearSelectedEvent())
    }
  }

  const handleCloseModalAfterSave = () => {
    setOpen(false)
    dispatch(clearSelectedEvent())
  }

  return (
    <>
      <Tooltip title='Update' arrow>
        <span>
          <IconButton
            id='update-event-button'
            color='primary'
            size='small'
            onClick={handleOpenModal}
            disabled={completed}
          >
            <EditRoundedIcon />
          </IconButton>
        </span>
      </Tooltip>

      <Dialog open={open} fullWidth fullScreen={fullScreen}>
        <DialogTitle
          sx={{
            background: gradientColors.greenGradient,
            color: textColors.white,
            fontWeight: 'bold',
            fontSize: fontSizes.size20,
          }}
        >
          Update Event
        </DialogTitle>
        <IconButton
          onClick={handleCloseModal}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
            color: textColors.white,
          }}
          size='small'
        >
          <CloseIcon />
        </IconButton>

        <DialogContent>
          {open && loading && (
            <>
              <Box
                component='div'
                sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2 }}
              >
                <CircularProgress />
                <Typography ml={2}>Fetching Event Details...</Typography>
              </Box>
            </>
          )}
          {open && eventDetails && !loading && (
            <EventForm
              onSave={handleCloseModalAfterSave}
              onChange={handleFormChanges}
              eventDetails={eventDetails}
            />
          )}
        </DialogContent>
      </Dialog>
      {ConfirmationAlert}
    </>
  )
}

export default EventUpdateModal
